import { css } from "@emotion/react";
import memoizeOne from "memoize-one";
import { useEffect, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { Dispatch } from "redux";

import { Add } from "@livechat/design-system-icons";
import { Icon, Info, SpacingToken, Tooltip } from "@livechat/design-system-react-components";

import { CustomerDetailsWidgetIcons, CustomerDetailsWidgets } from "components/customer-details";
import { ViewContainer } from "components/grid";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { newFeaturesCampaigns } from "constants/campaigns";
import { CUSTOMER_DETAILS_WIDGET_ID, CustomerDetailsPlacement } from "constants/customer-details";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { getIsMemberOfTicketTeam } from "helpers/tickets";
import { IWithBreakpointsProps, withBreakpoints } from "helpers/withBreakpoints";
import { useNewFeatureForAgents } from "hooks/use-new-feature-for-agents";
import { IFlattenedCustomSection } from "interfaces/customer-details";
import {
  IAssignmentAgent,
  IAssignmentTeam,
  ITicket,
  TicketPriority,
  TicketSilo,
  TicketStatus,
} from "interfaces/entities/ticket";
import { IStoreState } from "interfaces/storeState";
import { TicketsDetailsOpenedSections } from "routes/tickets/constants";
import { IButtonData } from "services/custom-section";
import { getItem, saveItem } from "services/local-storage";
import {
  ICustomerDetailsSectionMessage,
  IOutboxMessage,
  connectionPool,
  createCustomerDetailsSectionButtonClickMessage,
  createTicketInfoMessage,
} from "services/widget";
import { WidgetIntegrationActions } from "store/actions";
import { IAgent, selectIsAdmin, selectIsAgent, selectIsViewer, selectLoggedInAgent } from "store/entities/agents";
import { IWidget, getWidgetsWithPlacement } from "store/entities/applications";
import { ICustomField } from "store/entities/custom-fields/interfaces";
import { selectCustomFieldsList } from "store/entities/custom-fields/selectors";
import { selectFeatureToggleSimilarTickets, selectLicenseId } from "store/entities/license";
import { IReplyAddress } from "store/entities/reply-addresses/interfaces";
import { selectReplyAddresses } from "store/entities/reply-addresses/selectors";
import { ITag } from "store/entities/tags";
import { ITeam } from "store/entities/teams";
import { TicketActions } from "store/entities/tickets/actions";
import { ApplicationsActions } from "store/features/applications/actions";
import { selectLoadedWidgets } from "store/features/applications/selectors";
import { TicketsViewActions } from "store/views/tickets/actions";
import { selectCanManageTickets, selectRecentTickets } from "store/views/tickets/selectors";
import { withVar } from "themes/helpers";

import SpamListModal from "../SpamListModal";

import { TicketDetailsCustomFields } from "./TicketDetailsCustomFields";
import { TicketDetailsInfo } from "./TicketDetailsInfo";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-named-as-default
import TicketDetailsRecentTickets from "./TicketDetailsRecentTickets";
import { TicketDetailsRequester } from "./TicketDetailsRequester";
import { TicketDetailsResponsibility } from "./TicketDetailsResponsibility";
import { TicketDetailsSimilarTickets } from "./TicketDetailsSimilarTickets";
import { TicketDetailsWidgetOpening } from "./TicketDetailsWidgetOpening";
import {
  AddApplicationLink as AddApplicationButton,
  IconWrapper,
  StyledBackButton,
  StyledCloseButton,
  StyledContainer,
  StyledLine,
  StyledTitle,
  TicketsDetailsContainer,
  TicketsDetailsContent,
  TicketsDetailsWidgetsContainer,
  TitleWrapper,
  WidgetIcon,
  WidgetName,
  addApplicationIconStyles,
} from "./styles";
import TicketDetailsMoreDropdown from "./ticket-context-menu/TicketDetailsMoreDropdown";
import { TicketDetailsTags } from "./ticket-details-tags/TicketDetailsTags";

interface IOwnProps {
  ticket: ITicket;
  agents: IAgent[];
  teams: ITeam[];
  tags: ITag[];
  allTags: ITag[];
  isFetching: boolean;
  canManageTicket: boolean;
  canDeleteTicket: boolean;
  tagsUsageAllowed: boolean;
  onAssigneeChange(assigneeId: string): void;
  onAssignedTeamChange(teamId: string): void;
  onRequesterModalOpen(): void;
  onMergingModalOpen?(): void;
  onCcsModalOpen(): void;
  onTagAdd(tagId: string, suggested: boolean): void;
  onTagRemove(tagId: string): void;
  onStatusUpdate(status: TicketStatus): void;
  onTicketUrlCopy(): void;
  onDeleteTicket(): void;
  isAgentInTicketTeam?: boolean;
  isChildTicket: boolean;
  isMobile: boolean;
}

interface IStateProps {
  loadedWidgets: string[];
  widgets: ReturnType<typeof getWidgetsWithPlacement>;
  currentAgent: IAgent;
  licenseID: number;
  replyAddresses: Record<string, IReplyAddress>;
  recentTickets: ITicket[];
  customFields: ICustomField[];
  isAdmin: boolean;
  isOwner: boolean;
  canManageTickets: boolean;
}

interface IDispatchProps {
  onChangeSelectedWidget(widgetId: string): void;
  onSetFocusedColumn(focusedColumn: number): void;
  onPriorityUpdate(priority: TicketPriority): void;
  onRequesterEmailCopy(): void;
  // @ts-expect-error TS(7010): 'sendMessage', which lacks return-type annotation,... Remove this comment to see the full error message
  sendMessage(message: IOutboxMessage);
}

interface IProps extends IOwnProps, IStateProps, IDispatchProps {}

const TicketDetailsRightNodesRaw: React.FC<
  {
    ticket: ITicket;
    isMemberOfTicketTeam: boolean;
    canDeleteTicket: boolean;
    canManageTickets: boolean;
    onDeleteTicket(): void;
    setModalStatus(status: "spam" | "unspam" | null): void;
  } & IWithBreakpointsProps
> = ({ canDeleteTicket, onDeleteTicket, isMobile, ticket, canManageTickets, isMemberOfTicketTeam, setModalStatus }) => {
  const dispatch = useDispatch();
  const isOwner = useSelector(selectIsAdmin);
  const {
    isNewForAgent,
    featureFlagValue: hasSeenSiloTooltip,
    setAgentFlagCallback,
  } = useNewFeatureForAgents(
    newFeaturesCampaigns.hasSeenSiloTooltip.flag,
    newFeaturesCampaigns.hasSeenSiloTooltip.campaignStartDate
  );

  const shouldDisplaySiloTooltip = !hasSeenSiloTooltip && !isMobile && isNewForAgent;

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const onSiloChange = (silo: TicketSilo, ticketId: string) => {
    dispatch(TicketActions.updateSilo({ silo, ticketId }));
    if (silo === TicketSilo.Spam) {
      trackEngagement("Ticket: Blocked list modal opened on context menu change", EventPlace.Tickets);
      setModalStatus("spam");
    }

    if (silo !== TicketSilo.Spam && ticket.silo === TicketSilo.Spam) {
      trackEngagement("Ticket: Trusted list modal opened on context menu change", EventPlace.Tickets);
      setModalStatus("unspam");
    }
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleSiloTooltipClose = () => {
    setAgentFlagCallback();
    trackEngagement("Ticket details: Archives onboarding tooltip closed", EventPlace.Tickets);
  };

  const ticketDropdown = (
    <TicketDetailsMoreDropdown
      ticket={ticket}
      isOwner={isOwner}
      onSiloChange={onSiloChange}
      onDeleteTicket={onDeleteTicket}
      canManageTickets={canManageTickets}
      canDeleteTicket={canDeleteTicket}
      isMemberOfTicketTeam={isMemberOfTicketTeam}
    />
  );

  return (
    <Flex marginRight={withVar(SpacingToken.Spacing2)}>
      {shouldDisplaySiloTooltip ? (
        <Tooltip
          isVisible
          theme="important"
          placement="bottom-end"
          triggerRenderer={() => <Block onClick={handleSiloTooltipClose}>{ticketDropdown}</Block>}
        >
          <Block width="240px" css={{ textAlign: "start" }}>
            <Info
              header="New actions available"
              text="From now on, you can move tickets to Archive, Spam, and Trash folders to keep only relevant tickets on your dashboard."
              closeWithX
              handleCloseAction={handleSiloTooltipClose}
            />
          </Block>
        </Tooltip>
      ) : (
        ticketDropdown
      )}
    </Flex>
  );
};

const TicketDetailsRightNodes = withBreakpoints(TicketDetailsRightNodesRaw);

function calculateAssignee(agents: IAgent[], assignedAgent: IAssignmentAgent | null): IAgent | undefined {
  if (assignedAgent) {
    return agents.find(agent => agent.id === assignedAgent.id);
  }
  return undefined;
}
const getAssignee = memoizeOne(calculateAssignee);

function calculateAssignedTeam(assignedTeam: IAssignmentTeam, teams: ITeam[]): ITeam | undefined {
  return teams.find(team => team.id === assignedTeam.id) || teams.find(team => team.isDefault);
}
const getAssignedTeam = memoizeOne(calculateAssignedTeam);

export const TicketDetailsComponent: React.FC<IProps> = ({
  ticket,
  currentAgent,
  onChangeSelectedWidget,
  onSetFocusedColumn,
  sendMessage,
  agents,
  teams,
  tags,
  allTags,
  canManageTicket,
  canDeleteTicket,
  isFetching,
  tagsUsageAllowed,
  loadedWidgets,
  widgets,
  replyAddresses,
  licenseID,
  customFields,
  onAssigneeChange,
  onAssignedTeamChange,
  onRequesterModalOpen,
  onCcsModalOpen,
  onTagAdd,
  onTagRemove,
  onTicketUrlCopy,
  onDeleteTicket,
  onStatusUpdate,
  onPriorityUpdate,
  recentTickets,
  onMergingModalOpen,
  isAgentInTicketTeam,
  isChildTicket,
  isMobile,
  onRequesterEmailCopy,
}) => {
  const isViewer = useSelector(selectIsViewer);
  const isMemberOfTicketTeam = ticket && getIsMemberOfTicketTeam(currentAgent, ticket);
  const [selectedWidgetId, setSelectedWidgetId] = useState(CUSTOMER_DETAILS_WIDGET_ID);
  const [modalStatus, setModalStatus] = useState<"spam" | "unspam" | null>(null);
  const [openedSections, setOpenedSections] = useState({
    [TicketsDetailsOpenedSections.Info]: getItem(TicketsDetailsOpenedSections.Info) ?? true,
    [TicketsDetailsOpenedSections.Tags]: getItem(TicketsDetailsOpenedSections.Tags) ?? true,
    [TicketsDetailsOpenedSections.CustomFields]: getItem(TicketsDetailsOpenedSections.CustomFields) ?? true,
    [TicketsDetailsOpenedSections.Responsibility]: getItem(TicketsDetailsOpenedSections.Responsibility) ?? true,
    [TicketsDetailsOpenedSections.Requester]: getItem(TicketsDetailsOpenedSections.Requester) ?? true,
    [TicketsDetailsOpenedSections.Recent]: getItem(TicketsDetailsOpenedSections.Recent) ?? true,
    [TicketsDetailsOpenedSections.Widgets]: getItem(TicketsDetailsOpenedSections.Widgets) || {},
  });
  const [openingStateWidgets, setOpeningStateWidgets] = useState<IWidget[]>([]);
  const connections = connectionPool.getAll();
  const history = useHistory();

  const shouldShowSimilarTickets = useSelector(selectFeatureToggleSimilarTickets);

  useEffect(() => {
    setOpeningStateWidgets(widgets);
  }, [widgets]);

  useEffect(() => {
    widgets.forEach(widget => onChangeSelectedWidget(widget.id));
  }, [widgets, onChangeSelectedWidget]);

  useEffect(() => {
    if (ticket && connections.length > 0) {
      connections.forEach(connection => {
        sendMessage(createTicketInfoMessage(connection.id, ticket));
      });
    }
  }, [JSON.stringify(ticket), connections]);

  const getLoadedWidgets = memoizeOne((items: { id: string; name?: string; icon?: string }[], widgetIds: string[]) =>
    items.filter(w => widgetIds.includes(w.id))
  );

  const handleBack = (): void => {
    onSetFocusedColumn(0);
  };

  const handleCloseWidget = (): void => {
    setSelectedWidgetId(CUSTOMER_DETAILS_WIDGET_ID);
  };

  const handleChangeSelectedWidget = (widgetId: string): void => {
    setSelectedWidgetId(widgetId);
    onChangeSelectedWidget(widgetId);
  };

  const handleOpenedSection = (section: TicketsDetailsOpenedSections): void => {
    if (section !== TicketsDetailsOpenedSections.Widgets) {
      saveItem(section, !openedSections[section]);
    }
    setOpenedSections({
      ...openedSections,
      [section]: !openedSections[section],
    });
  };

  const handleOpenedWidgetSection = (widgetId: string): void => {
    saveItem(TicketsDetailsOpenedSections.Widgets, {
      ...openedSections[TicketsDetailsOpenedSections.Widgets],
      [widgetId]: !openedSections[TicketsDetailsOpenedSections.Widgets][widgetId],
    });

    setOpenedSections({
      ...openedSections,
      [TicketsDetailsOpenedSections.Widgets]: {
        ...openedSections[TicketsDetailsOpenedSections.Widgets],
        [widgetId]: !openedSections[TicketsDetailsOpenedSections.Widgets][widgetId],
      },
    });
  };

  const handleCustomSection = (message: ICustomerDetailsSectionMessage): void => {
    const { pluginId, data } = message;

    const updatedOpeningStateWidgets = openingStateWidgets.map(widget => {
      if (pluginId === widget.id) {
        return {
          ...widget,
          shortcut: {
            initialState: JSON.stringify({
              ticketDetailsSections: [data],
            }),
          },
        };
      }

      return widget;
    });

    setOpeningStateWidgets(updatedOpeningStateWidgets);
  };

  const handleCustomSectionButtonClick = (buttonData: IButtonData, section: IFlattenedCustomSection): void => {
    const message = createCustomerDetailsSectionButtonClickMessage(section.pluginId, buttonData.id);
    sendMessage(message);

    if (buttonData.openApp) {
      handleChangeSelectedWidget(section.pluginId);
    }
  };

  const handleAddApplicationClick = (): void => {
    trackEngagement("Ticket details: Marketplace opened", EventPlace.Tickets);
    history.push("/marketplace");
  };

  const isWidgetSelected = (id: string): boolean => selectedWidgetId === id;

  if (isFetching && !ticket) {
    return <StyledContainer />;
  }

  const assignee = getAssignee(agents, ticket.assignment.agent);
  const ticketDetailsSelected = isWidgetSelected(CUSTOMER_DETAILS_WIDGET_ID);
  const assignedTeam = getAssignedTeam(ticket.assignment.team, teams);
  const backButton = <StyledBackButton onClick={handleBack} />;
  const closeWidgetButton = <StyledCloseButton onClick={handleCloseWidget} />;

  const closeButton = ticketDetailsSelected ? backButton : closeWidgetButton;

  const followers = ticket.followers.map(followerID => agents.find(agent => agent.id === followerID)).filter(Boolean);
  const widgetsDetails = getLoadedWidgets(widgets, loadedWidgets);
  const loadedWidget = widgetsDetails.find(widget => widget.id === selectedWidgetId);
  const isRecentTicket = ticket.silo === TicketSilo.Tickets;
  const canEditTicketDetails = canManageTicket && isRecentTicket;

  const title =
    loadedWidget && !ticketDetailsSelected ? (
      <TitleWrapper>
        {loadedWidget.icon && (
          <IconWrapper>
            <WidgetIcon src={loadedWidget.icon} alt={loadedWidget.name} />
          </IconWrapper>
        )}
        <WidgetName>{loadedWidget.name}</WidgetName>
      </TitleWrapper>
    ) : (
      <StyledTitle>Details</StyledTitle>
    );

  const content = (
    <>
      {loadedWidgets.length > 0 && (
        <>
          <CustomerDetailsWidgets
            isVisible={!ticketDetailsSelected}
            // @ts-expect-error TS(2769)
            widgets={getLoadedWidgets(widgets, loadedWidgets)}
            selectedWidgetId={selectedWidgetId}
            context={{ ticketInfo: ticket }}
            onCustomerDetailsSectionChange={handleCustomSection}
          />
        </>
      )}
      {ticketDetailsSelected && (
        <TicketsDetailsContainer>
          <TicketsDetailsContent widgets={widgets}>
            <TicketDetailsInfo
              ticket={ticket}
              teams={teams}
              canManageTicket={canEditTicketDetails}
              currentAgent={currentAgent}
              onTicketUrlCopy={onTicketUrlCopy}
              onStatusUpdate={onStatusUpdate}
              onPriorityUpdate={onPriorityUpdate}
              opened={openedSections[TicketsDetailsOpenedSections.Info]}
              onHeaderClick={() => handleOpenedSection(TicketsDetailsOpenedSections.Info)}
            />
            <TicketDetailsTags
              ticket={ticket}
              tags={tags}
              allTags={allTags}
              canManageTicket={canEditTicketDetails}
              tagsUsageAllowed={tagsUsageAllowed}
              onTagAdd={onTagAdd}
              onTagRemove={onTagRemove}
              opened={openedSections[TicketsDetailsOpenedSections.Tags]}
              onHeaderClick={() => handleOpenedSection(TicketsDetailsOpenedSections.Tags)}
            />
            <TicketDetailsCustomFields
              ticket={ticket}
              currentAgent={currentAgent}
              customFields={customFields}
              opened={openedSections[TicketsDetailsOpenedSections.CustomFields]}
              onHeaderClick={() => handleOpenedSection(TicketsDetailsOpenedSections.CustomFields)}
            />
            <TicketDetailsResponsibility
              ticket={ticket}
              // @ts-expect-error TS(2769)
              followers={followers}
              loggedInAgent={currentAgent}
              teams={teams}
              canManageTicket={canEditTicketDetails}
              assignedTeam={assignedTeam}
              assignedAgent={assignee}
              onTeamChange={onAssignedTeamChange}
              onAgentChange={onAssigneeChange}
              replyAddresses={replyAddresses}
              licenseID={licenseID}
              isChildTicket={isChildTicket}
              isRecentTicket={isRecentTicket}
              opened={openedSections[TicketsDetailsOpenedSections.Responsibility]}
              onHeaderClick={() => handleOpenedSection(TicketsDetailsOpenedSections.Responsibility)}
            />
            <TicketDetailsRequester
              requester={ticket.requester}
              ccs={ticket.ccs}
              ticketSilo={ticket.silo}
              canEditRequester={canEditTicketDetails}
              canEditCcs={canManageTicket}
              onRequesterModalOpen={onRequesterModalOpen}
              onCcsModalOpen={onCcsModalOpen}
              opened={openedSections[TicketsDetailsOpenedSections.Requester]}
              onRequesterEmailCopied={onRequesterEmailCopy}
              onHeaderClick={() => handleOpenedSection(TicketsDetailsOpenedSections.Requester)}
            />
            <TicketDetailsRecentTickets
              currentTicket={ticket}
              onMergingModalOpen={onMergingModalOpen}
              tickets={recentTickets}
              isViewer={isViewer}
              isAgentInTicketTeam={isAgentInTicketTeam ?? false}
              isMobile={isMobile}
              opened={openedSections[TicketsDetailsOpenedSections.Recent]}
              onHeaderClick={() => handleOpenedSection(TicketsDetailsOpenedSections.Recent)}
            />
            {shouldShowSimilarTickets && <TicketDetailsSimilarTickets currentTicketId={ticket.id} />}
            {openingStateWidgets.length > 0 &&
              openingStateWidgets.map(widget => (
                <TicketDetailsWidgetOpening
                  key={widget.id}
                  widget={widget}
                  opened={openedSections[TicketsDetailsOpenedSections.Widgets][widget.id]}
                  onHeaderClick={() => handleOpenedWidgetSection(widget.id)}
                  onCustomSectionButtonClick={handleCustomSectionButtonClick}
                />
              ))}
          </TicketsDetailsContent>

          {widgets.length > 0 && (
            <TicketsDetailsWidgetsContainer>
              <>
                <CustomerDetailsWidgetIcons
                  widgets={widgets}
                  selectedWidgetId={selectedWidgetId}
                  hasCloseButton={false}
                  onChangeSelectedWidget={handleChangeSelectedWidget}
                  placement={CustomerDetailsPlacement.Tickets}
                />

                <StyledLine />

                <Tooltip
                  placement="left"
                  triggerRenderer={() => (
                    <AddApplicationButton onClick={handleAddApplicationClick} disabled={isViewer}>
                      <Icon source={Add} css={addApplicationIconStyles} />
                    </AddApplicationButton>
                  )}
                >
                  <div
                    css={css`
                      white-space: nowrap;
                    `}
                  >
                    Add application
                  </div>
                </Tooltip>
              </>
            </TicketsDetailsWidgetsContainer>
          )}
        </TicketsDetailsContainer>
      )}
    </>
  );

  return (
    <>
      <StyledContainer>
        <ViewContainer
          title={title}
          leftNodes={closeButton}
          contentCss={css`
            z-index: auto;
          `}
          rightNodes={
            !loadedWidget && (
              <TicketDetailsRightNodes
                canDeleteTicket={canDeleteTicket}
                onDeleteTicket={onDeleteTicket}
                ticket={ticket}
                canManageTickets={canManageTicket}
                isMemberOfTicketTeam={isMemberOfTicketTeam}
                setModalStatus={setModalStatus}
              />
            )
          }
        >
          {content}
        </ViewContainer>
      </StyledContainer>
      {modalStatus && (
        <SpamListModal
          modalType={modalStatus}
          requesterEmails={[ticket.requester.email]}
          onClose={() => setModalStatus(null)}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: IStoreState): IStateProps => ({
  loadedWidgets: selectLoadedWidgets(state),
  recentTickets: selectRecentTickets(state),
  widgets: getWidgetsWithPlacement(state, "plugin"),
  currentAgent: selectLoggedInAgent(state),
  isAdmin: selectIsAgent(state),
  isOwner: selectIsAdmin(state),
  replyAddresses: selectReplyAddresses(state),
  licenseID: selectLicenseId(state),
  customFields: selectCustomFieldsList(state),
  canManageTickets: selectCanManageTickets(state),
});

const mapDispatchToProps = (dispatch: Dispatch, ownProps: { ticket: ITicket }): IDispatchProps => ({
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  sendMessage(message: IOutboxMessage) {
    // @ts-expect-error TS(2345)
    dispatch(WidgetIntegrationActions.sendOutboxMessage(message));
  },
  // @ts-expect-error TS(2345)
  onChangeSelectedWidget: (widgetId: string) => dispatch(ApplicationsActions.loadWidget(widgetId)),
  // @ts-expect-error TS(2345)
  onSetFocusedColumn: (focusedColumn: number) => dispatch(TicketsViewActions.setFocusedColumn(focusedColumn)),
  onPriorityUpdate: (priority: TicketPriority) =>
    dispatch(
      // @ts-expect-error TS(2345)
      TicketsViewActions.updatePriority({ ticketId: ownProps.ticket.id, interaction: "ticketDetails", priority })
    ),
  // @ts-expect-error TS(2345)
  onRequesterEmailCopy: () => dispatch(TicketsViewActions.copyRequesterEmail()),
});

export const TicketDetails = connect(mapStateToProps, mapDispatchToProps)(TicketDetailsComponent);
